import type { UniqueIdentifier } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

import type { FlattenedItem, SurveySection, TreeItems } from '../types';

export const iOS = /iPad|iPhone|iPod/.test(navigator.platform);

function getDragDepth(offset: number, indentationWidth: number) {
  return Math.round(offset / indentationWidth);
}

export function getProjection(
  items: FlattenedItem[],
  activeId: UniqueIdentifier,
  overId: UniqueIdentifier,
  dragOffset: number,
  indentationWidth: number
) {
  const overItemIndex = items.findIndex(({ id }) => id === overId);
  const activeItemIndex = items.findIndex(({ id }) => id === activeId);
  const activeItem = items[activeItemIndex];
  const newItems = arrayMove(items, activeItemIndex, overItemIndex);
  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];
  const dragDepth = getDragDepth(dragOffset, indentationWidth);
  const projectedDepth = activeItem.depth + dragDepth;
  const maxDepth = getMaxDepth({
    previousItem,
  });
  const minDepth = getMinDepth({ nextItem });
  let depth = projectedDepth;

  if (projectedDepth >= maxDepth) {
    depth = maxDepth;
  } else if (projectedDepth < minDepth) {
    depth = minDepth;
  }

  return { depth, maxDepth, minDepth, parentId: getParentId() };

  function getParentId() {
    if (depth === 0 || !previousItem) {
      return null;
    }

    if (depth === previousItem.depth) {
      return previousItem.parentId;
    }

    if (depth > previousItem.depth) {
      return previousItem.id;
    }

    const newParent = newItems
      .slice(0, overItemIndex)
      .reverse()
      .find((item) => item.depth === depth)?.parentId;

    return newParent ?? null;
  }
}

function getMaxDepth({ previousItem }: { previousItem: FlattenedItem }) {
  if (previousItem) {
    return previousItem.depth + 1;
  }

  return 0;
}

function getMinDepth({ nextItem }: { nextItem: FlattenedItem }) {
  if (nextItem) {
    return nextItem.depth;
  }

  return 0;
}

function flatten(
  items: TreeItems,
  parentId: UniqueIdentifier | null = null,
  depth = 0
): FlattenedItem[] {
  return items.reduce<FlattenedItem[]>((acc, item, index) => {
    return [
      ...acc,
      { ...item, parentId, depth, index },
      ...flatten(item.subSections, item.id, depth + 1),
    ];
  }, []);
}

export function flattenTree(items: TreeItems): FlattenedItem[] {
  return flatten(items);
}

export function buildTree(flattenedItems: FlattenedItem[]): TreeItems {
  const root: Partial<SurveySection> = { id: 'root', subSections: [] };
  const nodes: Record<string, SurveySection> = { [root.id as string]: root as SurveySection };
  const items = flattenedItems.map((item) => ({ ...item, subSections: [] }));

  for (const item of items) {
    const { id } = item;
    const parentId = item.parentId ?? (root.id as UniqueIdentifier);
    const parent = nodes[parentId] ?? findItem(items, parentId);

    nodes[id] = { ...item };
    parent?.subSections?.push(item);
  }

  return root?.subSections as TreeItems;
}

export function findItem(items: SurveySection[], itemId: UniqueIdentifier) {
  return items.find(({ id }) => id === itemId);
}

export function findItemDeep(
  items: TreeItems,
  itemId: UniqueIdentifier
): SurveySection | undefined {
  for (const item of items) {
    const { id, subSections } = item;

    if (id === itemId) {
      return item;
    }

    if (subSections.length) {
      const child = findItemDeep(subSections, itemId);

      if (child) {
        return child;
      }
    }
  }

  return undefined;
}

export function removeItem(items: TreeItems, id: UniqueIdentifier) {
  const newItems = [];

  for (const item of items) {
    if (item.id === id) {
      continue;
    }

    if (item.subSections.length) {
      item.subSections = removeItem(item.subSections, id);
    }

    newItems.push(item);
  }

  return newItems;
}

export function insertItem(items: TreeItems, item: SurveySection) {
  return items.map((section) => {
    if (section.id === item.parentId) {
      section.subSections.push(item);
    } else if (section.subSections.length) {
      section.subSections = insertItem(section.subSections, item);
    }
    return section;
  });
}

export function setProperty<T extends keyof SurveySection>(
  items: TreeItems,
  id: UniqueIdentifier,
  property: T,
  setter: (value: SurveySection[T]) => SurveySection[T]
) {
  for (const item of items) {
    if (item.id === id) {
      item[property] = setter(item[property]);
      continue;
    }

    if (item.subSections.length) {
      item.subSections = setProperty(item.subSections, id, property, setter);
    }
  }

  return [...items];
}

function countChildren(items: SurveySection[], count = 0): number {
  return items.reduce((acc, { subSections }) => {
    if (subSections.length) {
      return countChildren(subSections, acc + 1);
    }

    return acc + 1;
  }, count);
}

export function getChildCount(items: TreeItems, id: UniqueIdentifier) {
  const item = findItemDeep(items, id);

  return item ? countChildren(item.subSections) : 0;
}

export function removeChildrenOf(items: FlattenedItem[], ids: UniqueIdentifier[]) {
  const excludeParentIds = [...ids];

  return items.filter((item) => {
    if (item.parentId && excludeParentIds.includes(item.parentId)) {
      if (item.subSections.length) {
        excludeParentIds.push(item.id);
      }
      return false;
    }

    return true;
  });
}
